import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Badge,
  Button,
  Spinner,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { ToastContainer } from 'react-toastify';
import useTenderOpeningTabVisibility from '../hooks/useTenderOpeningTabVisibility';
import useTenderOpeningList from '../hooks/useTenderOpeningList';
import { getTenderOpeningBadgeColor } from '../utils/tenderOpeningHelpers';

const OPENING_STATUS_META = {
  Open: { color: 'primary', label: 'Open' },
  'In Progress': { color: 'warning', label: 'In Progress' },
  Done: { color: 'success', label: 'Done' },
};

const StatusPill = ({ wording, metaMap, fallbackColor = 'secondary' }) => {
  if (!wording) return <span className='text-muted'>-</span>;

  const meta = metaMap[wording] || {};
  const color =
    meta.color || getTenderOpeningBadgeColor(wording) || fallbackColor;
  const label = meta.label || wording;

  return (
    <Badge
      color={color}
      className='d-inline-block px-2 py-1 fs-12 fw-medium rounded-pill text-wrap text-start'
      style={{
        lineHeight: 1.35,
        maxWidth: '100%',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
      title={wording}
    >
      {label}
    </Badge>
  );
};

const renderTrackIcon = (val, isEnabled = true) => {
  if (!isEnabled) {
    return <span className='text-muted'>-</span>;
  }

  if (val === true) {
    return (
      <i
        className='ri-checkbox-circle-fill text-success fs-18'
        title='Done'
        aria-label='Done'
      />
    );
  }

  if (val === false) {
    return (
      // <i
      //   className='ri-error-warning-fill text-warning fs-18'
      //   title='In Progress'
      //   aria-label='In Progress'
      // />
      <i
        className='ri-checkbox-blank-circle-fill text-secondary border rounded-circle fs-18'
        title='Open'
        aria-label='Open'
      />
    );
  }

  return (
    <i
      className='ri-checkbox-blank-circle-fill text-secondary border rounded-circle fs-18'
      title='Open'
      aria-label='Open'
    />
  );
};

const TenderOpeningTable = ({ procurementTypeHeader, refreshTrigger = 0 }) => {
  const navigate = useNavigate();
  const { canViewProcurementTypeHeader } = useTenderOpeningTabVisibility();
  const canViewTable = canViewProcurementTypeHeader(procurementTypeHeader);

  const {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    tenderOpeningStatusFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
  } = useTenderOpeningList({
    procurementTypeHeader,
    refreshTrigger,
    enabled: canViewTable,
  });

  if (!canViewTable) {
    return (
      <CardBody className='pt-0'>
        <p className='text-muted small mb-0 py-4 text-center'>
          You do not have access to view this procurement category.
        </p>
      </CardBody>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const MobileCard = ({ item }) => (
    <div className='card mb-3 d-md-none border shadow-none'>
      <div className='card-body p-3'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <span className='text-primary fw-semibold fs-13'>{item.idProc}</span>
          <StatusPill wording={item.status} metaMap={OPENING_STATUS_META} />
        </div>

        <h6 className='card-title fw-bold mb-1 text-dark lh-base'>
          {item.title}
        </h6>
        <p className='text-muted fs-12 mb-2'>
          {item.productType?.main} <span className='mx-1'>•</span>{' '}
          {item.productType?.sub}
        </p>

        <div className='d-flex flex-wrap gap-3 mb-3 pb-3'>
          <div>
            <span className='text-muted fs-11 text-uppercase d-block mb-1'>
              Submitted
            </span>
            <span className='fs-12 fw-medium text-dark'>
              {item.submittedDate}
            </span>
          </div>
          <div>
            <span className='text-muted fs-11 text-uppercase d-block mb-1'>
              Update Date
            </span>
            <span className='fs-12 fw-medium text-dark'>{item.updateDate}</span>
          </div>
          <div>
            <span className='text-muted fs-11 text-uppercase d-block mb-1'>
              Proc Type
            </span>
            <span className='fs-12 fw-medium text-dark'>{item.procType}</span>
          </div>
          <div>
            <span className='text-muted fs-11 text-uppercase d-block mb-1'>
              Period
            </span>
            <span className='fs-12 fw-medium text-dark'>{item.period}</span>
          </div>
        </div>

        <div className='row g-2 mb-3'>
          {[
            { label: 'Req HPS', value: item.reqHps },
            { label: 'Invite DMUT', value: item.inviteDmut },
            { label: 'Panitia', value: item.invitePanitia },
            {
              label: 'Req Freight',
              value: item.reqFreight,
              isEnabled: item.isReqFreightAvailabilityEnable,
              hideIfNull: true,
            },
          ]
            .filter(track => !track.hideIfNull || track.value !== null)
            .map(track => (
              <div className='col-6' key={track.label}>
                <div className='d-flex align-items-center justify-content-between border rounded p-2 bg-light h-100'>
                  <span className='fs-11 fw-medium text-muted'>
                    {track.label}
                  </span>
                  {renderTrackIcon(track.value, track.isEnabled)}
                </div>
              </div>
            ))}
        </div>

        <Button
          color='light'
          size='sm'
          className='w-100 border bg-white shadow-none text-dark fw-medium py-2'
          onClick={() => navigate(`/apps-tenderopening/dtl/${item.id}`)}
        >
          <i className='ri-eye-line align-bottom me-1 text-muted'></i> View
          Detail
        </Button>
      </div>
    </div>
  );

  const columns = [
    {
      id: 'idProc',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>ID Proc</span>
      ),
      selector: row => row.idProc,
      cell: row => <span className='text-dark'>{row.idProc}</span>,
      width: '7%',
      sortable: true,
    },
    {
      id: 'submittedDate',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Submitted Date
        </span>
      ),
      selector: row => row.submittedDate,
      cell: row => <span className='text-dark'>{row.submittedDate}</span>,
      width: '8%',
      sortable: true,
    },
    {
      id: 'updateDate',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Update Date
        </span>
      ),
      selector: row => row.updateDateRaw,
      cell: row => <span className='text-dark'>{row.updateDate}</span>,
      width: '8%',
      sortable: true,
    },
    {
      id: 'title',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Title</span>
      ),
      selector: row => row.title,
      cell: row => (
        <span className='text-dark text-truncate' title={row.title}>
          {row.title}
        </span>
      ),
      width: '12%',
      sortable: true,
    },
    {
      id: 'period',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Period</span>
      ),
      selector: row => row.period,
      cell: row => <span className='text-dark'>{row.period}</span>,
      width: '5%',
      sortable: true,
    },
    {
      id: 'procType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Proc Type
        </span>
      ),
      selector: row => row.procType,
      cell: row => <span className='text-dark'>{row.procType}</span>,
      width: '9%',
      sortable: true,
    },
    {
      id: 'productType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Product Type
        </span>
      ),
      selector: row => row.productType?.main,
      cell: row => (
        <div className='d-flex flex-column justify-content-center py-2'>
          <span className='fw-medium text-dark'>{row.productType?.main}</span>
          <span className='text-muted small' style={{ fontSize: '11px' }}>
            {row.productType?.sub}
          </span>
        </div>
      ),
      width: '10%',
      sortable: true,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted text-center w-100'>
          Request
          <br />
          HPS
        </span>
      ),
      cell: row => (
        <div className='text-center w-100'>{renderTrackIcon(row.reqHps)}</div>
      ),
      width: '5%',
      center: true,
      sortable: false,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted text-center w-100'>
          Invite
          <br />
          DMUT
        </span>
      ),
      cell: row => (
        <div className='text-center w-100'>
          {renderTrackIcon(row.inviteDmut)}
        </div>
      ),
      width: '5%',
      center: true,
      sortable: false,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted text-center w-100'>
          Invite Panitia
          <br />
          Tender
        </span>
      ),
      cell: row => (
        <div className='text-center w-100'>
          {renderTrackIcon(row.invitePanitia)}
        </div>
      ),
      width: '6%',
      center: true,
      sortable: false,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted text-center w-100'>
          Req Freight
          <br />
          Availability
        </span>
      ),
      cell: row => (
        <div className='text-center w-100'>
          {renderTrackIcon(row.reqFreight, row.isReqFreightAvailabilityEnable)}
        </div>
      ),
      width: '7%',
      center: true,
      sortable: false,
    },
    {
      id: 'status',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Status</span>
      ),
      selector: row => row.status,
      cell: row => (
        <StatusPill wording={row.status} metaMap={OPENING_STATUS_META} />
      ),
      width: '8%',
      sortable: true,
      wrap: true,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted text-center w-100'>
          Action
        </span>
      ),
      cell: row => (
        <div className='text-center w-100'>
          <button
            className='btn btn-soft-primary btn-sm btn-icon'
            onClick={() => navigate(`/apps-tenderopening/dtl/${row.id}`)}
            title='Detail'
          >
            <i className='ri-eye-line align-bottom'></i>
          </button>
        </div>
      ),
      width: '10%',
      center: true,
      sortable: false,
    },
  ];

  return (
    <>
      <ToastContainer closeButton={false} />
      <CardBody className='pt-0'>
        <Row className='mb-3'>
          <Col xs={12}>
            <Row className='g-3'>
              <Col xs={12} md={6} lg={3}>
                <Label className='form-label'>Search</Label>
                <Input
                  type='text'
                  placeholder='Search by title or procurement no...'
                  value={searchText}
                  onChange={handleSearch}
                  className='form-control'
                />
              </Col>
              <Col xs={12} md={6} lg={3}>
                <Label className='form-label'>Status</Label>
                <Input
                  type='select'
                  value={tenderOpeningStatusFilter}
                  onChange={e => handleStatusFilter(e.target.value)}
                  className='form-control'
                >
                  <option value=''>All Status</option>
                  <option value='0'>Open</option>
                  <option value='1'>In Progress</option>
                  <option value='2'>Done</option>
                </Input>
              </Col>
              <Col xs={12} md={6} lg={3} className='d-flex align-items-end'>
                <Button
                  color='danger'
                  outline
                  onClick={clearFilters}
                  className='w-100'
                >
                  <i className='ri-refresh-line me-1'></i>
                  <span className='d-none d-sm-inline'>Clear Filters</span>
                  <span className='d-sm-none'>Clear</span>
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className='mb-2'>
          <Col xs={12}>
            <small className='text-muted'>
              {isLoading
                ? 'Loading...'
                : `Showing ${startEntry} to ${endEntry} of ${totalRows} entries`}
            </small>
          </Col>
        </Row>

        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner color='primary' />
            <p className='text-muted small mt-2 mb-0'>
              Loading tender opening data...
            </p>
          </div>
        ) : (
          <>
            <div className='d-md-none mt-3'>
              {data.items?.length > 0 ? (
                data.items.map((item, index) => (
                  <MobileCard key={item.id || index} item={item} />
                ))
              ) : (
                <div className='table-empty text-center py-4'>
                  <i
                    className='ri-inbox-line text-muted'
                    style={{ fontSize: '3rem', opacity: 0.5 }}
                  ></i>
                  <p className='mt-2 text-muted'>No procurement data found</p>
                </div>
              )}
            </div>

            <div className='d-none d-md-block'>
              <div
                className='table-responsive'
                style={{ maxHeight: '600px', overflowY: 'auto' }}
              >
                <DataTable
                  columns={columns}
                  data={data.items}
                  pagination={false}
                  onSort={handleSort}
                  sortServer
                  defaultSortFieldId='idProc'
                  defaultSortAsc={false}
                  highlightOnHover
                  responsive
                  striped
                  persistTableHead
                  dense
                  customStyles={{
                    header: {
                      style: {
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        fontWeight: 'bold',
                        minHeight: '40px',
                      },
                    },
                    headCells: {
                      style: {
                        backgroundColor: '#f0f0f0',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                      },
                    },
                    cells: {
                      style: {
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        wordBreak: 'break-word',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                      },
                    },
                    rows: { style: { minHeight: '45px' } },
                    table: { style: { width: '100%' } },
                    tableWrapper: { style: { display: 'table' } },
                  }}
                />
              </div>
            </div>
          </>
        )}

        <div className='d-flex justify-content-between align-items-center mt-3 px-3 py-2 border-top'>
          <div className='d-flex align-items-center'>
            <span className='text-muted me-2'>Rows per page:</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className='d-flex align-items-center'>
            <span className='text-muted me-3'>
              {startEntry}-{endEntry} of {totalRows}
            </span>
            <div className='btn-group' role='group'>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage <= 1 || isLoading}
                onClick={() => handlePageChange(1)}
              >
                <i className='ri-skip-back-line'></i>
              </button>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage <= 1 || isLoading}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className='ri-arrow-left-s-line'></i>
              </button>
              <span className='btn btn-outline-secondary btn-sm disabled'>
                {currentPage}
              </span>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage >= totalPages || isLoading}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className='ri-arrow-right-s-line'></i>
              </button>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage >= totalPages || isLoading}
                onClick={() => handlePageChange(totalPages)}
              >
                <i className='ri-skip-forward-line'></i>
              </button>
            </div>
          </div>
        </div>
      </CardBody>
    </>
  );
};

export default React.memo(TenderOpeningTable);
